/**
 * 🧍 **The other people** — T173, protocol 26.
 *
 * 🎯 **haugur, 2026-09-07:** *"we need to build a caracter so people can see
 * each other in the game. if not people would only see block appearing but no
 * one building them."*
 *
 * ## 🔴 What is deliberately NOT here yet
 *
 * ⬜ **The body is a stand-in.** 🎯 haugur has chosen **a photographed 3D model,
 * like the turf house**, and that is its own step with its own picture for him
 * to look at first — `Development/Minecraft/16` §10 is blunt that a scan is a
 * *different thing* from a Minecraft skin rather than a simpler one. **What is
 * built here is everything around the model**: who exists, where they are, which
 * way they face, and how that is made to look smooth.
 *
 * ✅ **THE NAME ABOVE THE HEAD IS BUILT** — T176, protocol 27. ⚠️ **This
 * paragraph said it was not, and gave two reasons: no name on the wire, and
 * that drawing one needs a textured material.** 🔴 **The first was true and is
 * fixed. The second was WRONG, and it is the more useful half.** Drawing text
 * over the world needs no texture and no material at all: `hud.ts` has floated
 * street names over the terrain since the beginning by projecting a world point
 * to screen space and putting a plain HTML element there. ✅ **The nameplates
 * go through that same projection**, so the change that was feared to be the
 * one that blacked out both environments turned out not to touch the renderer.
 *
 * 👉 **What made this findable was looking for the thing already on screen that
 * does the job**, rather than for the feature by name.
 *
 * ⬜ **What that costs, and it is real:** the HUD overlay has no depth buffer,
 * so **a name does not go behind a hill.** It is hidden past 30 m and when the
 * head is off screen — 🎯 haugur's number — and that is a stated limit rather
 * than something nobody noticed.
 *
 * ## 🔴 The two ways this goes wrong, both found in the research first
 *
 * 1. **Yaw wraps and a straight interpolation does not** — see
 *    {@link turnTowards}. Minecraft never meets it because its rotation is a
 *    byte; **ours is a float in radians, so the wrap is ours to handle.** The
 *    bug looks like a person pirouetting on the spot.
 * 2. **A frozen body and a departed body look identical.** The only thing that
 *    removes somebody is the server saying they are gone — which is why an
 *    empty list is a message that has to be sent rather than silence.
 * 3. 🔴 **A world coordinate is not a render coordinate.** Bodies were placed at
 *    their true ISN93 metres and drawn **347 km outside the world**. 🎯 *"dont
 *    see nobody."* **The server was sending perfectly the whole time** — proved
 *    with two plain WebSocket clients, which is the instrument that separated
 *    the two halves in one run. See {@link OtherPlayers.place}.
 */

import {
  Box3,
  BoxGeometry,
  BufferGeometry,
  Color,
  Float32BufferAttribute,
  Group,
  Mesh,
  Vector3,
  type Object3D,
  type Texture,
} from 'three';
import { MeshLambertNodeMaterial } from 'three/webgpu';
import { texture, uniform, uv, vec3 } from 'three/tsl';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { approach, turnTowards, type PlayersMessage } from '@lofi/shared';
import { BODY, HEAD_COLOUR, TORSO_COLOUR } from './playerbody.ts';
import { LEG_CUT, legSwing, splitLegs } from './legsplit.ts';
import { nameplateAt } from './nameplate.ts';
import { stamped } from './assets.ts';

/**
 * ⏱️ **How quickly a body catches up with where it really is, in seconds to
 * cover half the distance.**
 *
 * 🔴 **This is deliberate lateness.** Positions arrive ten times a second; a
 * body that jumped to each one would twitch. **Smoothing trades about a tenth
 * of a second of truth for movement that reads as walking** — the same bargain
 * every game makes, and `Development/Minecraft/16` §8 says Minecraft's is
 * bigger than ours.
 *
 * ⚠️ **It must never be applied to anything the server judges.** Nobody's reach,
 * nothing's collision and no click depends on where a body is drawn.
 */
const CATCH_UP_SECONDS = 0.08;

/**
 * 🗿 **The settler**, served beside the búr's own model as a plain git blob.
 *
 * ⚠️ **`packages/client/public/**` is excluded from LFS on purpose** — a file
 * the game SERVES must not be in LFS, because CI checks out pointers and the
 * player would download a text file that says where the model is.
 */
const SETTLER_URL = stamped('/models/player.glb');

/**
 * 🦵 **The two leg pivots, by name** — T173.
 *
 * ⚠️ **Found by name on each CLONE.** `clone()` copies the whole graph, so
 * every person has their own pair and they must be looked up per body rather
 * than held once. **Holding the template's pivots would make everybody walk in
 * step with whoever moved last.**
 */
const LEG_LEFT = 'leg-left';
const LEG_RIGHT = 'leg-right';

interface Body {
  readonly object: Object3D;
  /**
   * 🧍 **What to call them, or `null` for a connection with no account** —
   * T176, protocol 27.
   *
   * ⚠️ **Not `readonly`.** A name can change under a body that never left:
   * an anonymous connection is not the one that signs in, but a display name
   * can be edited on the website mid-session, and a body that kept its first
   * name would go on being right until somebody looked.
   */
  name: string | null;
  /** Where the body is drawn now — chases {@link target}. */
  x: number;
  y: number;
  z: number;
  yaw: number;
  /** Where the server last said they are. */
  targetX: number;
  targetY: number;
  targetZ: number;
  targetYaw: number;
  /**
   * 🚶 **How far this person has walked in total, in metres** — T173.
   *
   * 🔴 **The swing is a sine of DISTANCE, not of time** — so a slow walk swings
   * slowly and a run swings fast **without a second number to tune**, and
   * standing still is standing still rather than treading air.
   */
  walked: number;
  /** The two leg pivots on this body's own copy, or `null` for a stand-in. */
  left: Object3D | null;
  right: Object3D | null;
  /**
   * 🙌 **The species in this person's arms, or `null`** — T261, protocol 37.
   *
   * ⚠️ **The KIND, not an id.** Everybody draws *a sheep*; nobody needs to know
   * which one.
   */
  carrying: string | null;
  /** 🐣 Protocol 49 — how big, if it is a lamb; `undefined` for grown. */
  carryingLamb: number | undefined;
  /** 🛏️ Protocol 50 — lying in a bed: `x y z` is the mattress, `yaw` runs head to foot. */
  lying: boolean;
  /**
   * 🙌 **The animal actually drawn in their arms, or `null`.**
   *
   * 🔴 **BORROWED FROM THE FLOCK'S TEMPLATE AND NEVER DISPOSED.** It shares its
   * geometry and materials, exactly as a cloned settler body does — and freeing
   * a borrowed body is the fault that once made every arriving player
   * invisible.
   */
  held: Object3D | null;
  /**
   * 🔴 **Whether this body OWNS its geometry and materials, and may free them.**
   *
   * 🎯 **haugur, 2026-09-08:** *"if i playing and another users logs on, i have
   * to hit F5 in my browser to see that new user"* — and, the same day,
   * *"Lord haugur can see Lordhaugurtest but not the other way around."*
   *
   * 🔴 **Both were the same fault, and it is this one.** A stand-in builds its
   * own boxes and materials, so it must free them. **A settler is
   * `settlerTemplate.clone()`, and a three.js clone SHARES its geometry and
   * material with the original** — deliberately, to save memory. Disposing one
   * therefore destroyed the template **for everybody**, and every arrival after
   * the first departure was drawn from freed GPU memory: nothing at all.
   *
   * ⚠️ **The trigger is somebody LEAVING, not somebody joining**, which is why
   * it looked like "new users are invisible" and why F5 fixed it — a reload
   * fetches the model again.
   */
  owned: boolean;
}

/** One stand-in person: a torso with a head on it, feet at the group's origin. */
/**
 * 🗿 **The photographed settler** — the model, loaded once and cloned per person.
 *
 * 🎯 **haugur chose picture 1 of four**, 2026-09-07, and asked for *"a
 * photographed 3D model, like the turf house"*.
 *
 * 🔴 **RE-DRESSED, never drawn as it arrives.** A GLB comes with
 * `MeshStandardMaterial`; **this is the same `MeshLambertNodeMaterial` +
 * `texture()` colour node the búr's own walls use**, which is the one textured
 * path the running game already compiles. ⚠️ **A texture on a material path
 * nobody had run there is what turned dev AND staging black on 2026-09-01, and
 * a shader cannot be checked on this workstation at all.**
 */
let settlerTemplate: Object3D | null = null;
const waitingForSettler: (() => void)[] = [];

/** Fetch the settler once. ⚠️ A body built before it lands gets the stand-in. */
function loadSettler(): void {
  new GLTFLoader()
    .loadAsync(SETTLER_URL)
    .then((gltf) => {
      const scan = gltf.scene;
      /* 📏 Scaled by HEIGHT — a person is defined by how tall they are, where
         the búr was scaled by its 5 m width because a building is. */
      const box = new Box3().setFromObject(scan);
      const size = box.getSize(new Vector3());
      const centre = box.getCenter(new Vector3());
      const tall = BODY.torsoHeight + BODY.headSize;
      const scale = tall / Math.max(size.y, 1e-6);

      /**
       * 🦵 **Cut into a body and two legs, so he can walk** — T173.
       *
       * 🎯 **haugur, seeing him glide:** *"the carachter dont move limbs, they
       * just glide around like goshts hehe."* 🔴 **A photogrammetry scan is a
       * statue**; the way out is Minecraft's own — **rigid pieces rotated about
       * pivots, no skeleton** (`Development/Minecraft/16` §11.2).
       */
      scan.updateMatrixWorld(true);
      const cutY = (box.max.y - box.min.y) * LEG_CUT;
      const parts = {
        body: { p: [] as number[], u: [] as number[] },
        left: { p: [] as number[], u: [] as number[] },
        right: { p: [] as number[], u: [] as number[] },
      };
      let map: Texture | null = null;
      const v = new Vector3();
      scan.traverse((o) => {
        const mesh = o as Mesh;
        if (mesh.isMesh !== true) return;
        const old = mesh.material as { map?: Texture | null };
        if (old.map && map === null) map = old.map;
        const geom = mesh.geometry.index ? mesh.geometry.toNonIndexed() : mesh.geometry;
        const pos = geom.getAttribute('position');
        const uvAttr = geom.getAttribute('uv');
        /* 🔴 Into the frame everything else about this character is in: the
           model's own units, FEET AT y = 0 and centred on x/z. Splitting in a
           mesh's local frame would cut a different height on every part. */
        const flat: number[] = [];
        for (let i = 0; i < pos.count; i++) {
          v.fromBufferAttribute(pos, i).applyMatrix4(mesh.matrixWorld);
          flat.push(v.x - centre.x, v.y - box.min.y, v.z - centre.z);
        }
        const uvFlat = uvAttr
          ? // eslint-disable-next-line security/detect-object-injection -- a loop counter into a typed array.
            Array.from({ length: uvAttr.count * 2 }, (_, i) => uvAttr.array[i] ?? 0)
          : null;
        /* ⚠️ **The middle is 0 HERE**, because every vertex above had
           `centre.x` subtracted from it. It is not an assumption that the scan
           is centred on the origin — which is the fault `legsplit.test.ts`
           guards against. */
        const piece = splitLegs(flat, uvFlat, cutY, 0);
        for (const key of ['body', 'left', 'right'] as const) {
          /* eslint-disable security/detect-object-injection -- the same three
             literals, iterated from a const tuple; `burmesh.ts` does the same. */
          /* 🔴 A LOOP, NOT A SPREAD. `push(...array)` passes every element as
             an argument, and this model has 26 000 vertices — **`Maximum call
             stack size exceeded`**, which is a crash rather than a slow path.
             ⚠️ **Caught by baking the picture**, in the tool that shares this
             function, before it ever ran in the game. */
          for (const n of piece[key].p) parts[key].p.push(n);
          for (const n of piece[key].u) parts[key].u.push(n);
          /* eslint-enable security/detect-object-injection */
        }
      });

      /* 🔴 ONE material for all three pieces — the same
         `MeshLambertNodeMaterial` + `texture()` node the búr's walls use, which
         is the one textured path the running game already compiles. */
      const material = new MeshLambertNodeMaterial();
      if (map !== null) {
        const tile = texture(map, uv());
        material.colorNode = vec3(tile.r, tile.g, tile.b);
      } else {
        /* ⚠️ No texture would draw BLACK and nothing would warn — the fault
           that cost a model generation on 2026-09-07. */
        material.colorNode = uniform(new Color(TORSO_COLOUR));
      }

      const build = (part: { p: number[]; u: number[] }): Mesh => {
        const g = new BufferGeometry();
        g.setAttribute('position', new Float32BufferAttribute(part.p, 3));
        if (part.u.length > 0) g.setAttribute('uv', new Float32BufferAttribute(part.u, 2));
        g.computeVertexNormals();
        return new Mesh(g, material);
      };

      const assembled = new Group();
      assembled.add(build(parts.body));
      for (const side of ['left', 'right'] as const) {
        /* 🔴 The geometry is moved DOWN by the cut and the pivot moved UP by
           it, so the leg turns about the hem instead of about the floor. Get
           this backwards and the legs sweep like windscreen wipers. */
        // eslint-disable-next-line security/detect-object-injection -- a literal from a const tuple.
        const leg = build(parts[side]);
        leg.geometry.translate(0, -cutY, 0);
        const pivot = new Group();
        pivot.position.y = cutY;
        pivot.name = side === 'left' ? LEG_LEFT : LEG_RIGHT;
        pivot.add(leg);
        assembled.add(pivot);
      }

      const sized = new Group();
      sized.add(assembled);
      sized.scale.setScalar(scale);
      /**
       * 🔴 **The scan faces the OPPOSITE way to the yaw we are given.**
       *
       * ⚠️ **Found in a baked picture, not by reasoning**: the frame taken from
       * behind showed his face. The photograph was taken facing the camera and
       * the scan kept that, so **without this every player walks backwards.**
       *
       * 🔴 **AND IT MUST LIVE ON THE INNER NODE, NOT ON THE ROOT.** 🎯 haugur,
       * 2026-09-08, of two players face to face: *"looking at each other but
       * kinda not… it's like the camera angles are a little off."*
       *
       * ⚠️ **{@link OtherPlayers.place} ASSIGNS `root.rotation.y = body.yaw`
       * every frame**, so a correction stored on the root is wiped on the first
       * one and every body is drawn **180° from where that person is really
       * looking**. It survived on the template and died the moment anybody was
       * placed — which is why the model looked right in a design sheet and
       * wrong in the game.
       *
       * ✅ `sized` sits at the origin, so turning it is visually identical to
       * turning the root — and nothing overwrites it.
       */
      sized.rotation.y = Math.PI;

      /* 🔴 The root's `rotation.y` belongs to `place()` and must stay 0 here. */
      const facing = new Group();
      facing.add(sized);
      settlerTemplate = facing;
      for (const fill of waitingForSettler.splice(0)) fill();
    })
    .catch((err: unknown) => {
      /* ⚠️ Loud, and then everybody keeps the stand-in. A missing character is
         a worse game; a crash is no game. */
      console.error('[players] the settler model did not load', err);
    });
}

/**
 * One body, and whether it owns what it is made of.
 *
 * 🔴 **`owned` is the whole fix for the vanishing-settler bug** — see
 * {@link Body.owned}. A clone borrows the template's geometry and material;
 * only a stand-in may free its own.
 */
interface Built {
  readonly object: Object3D;
  readonly owned: boolean;
}

function buildBody(): Built {
  /* ⚠️ A clone SHARES geometry and material with the template. Nothing here
     may ever be disposed, or the next person to arrive is invisible. */
  if (settlerTemplate !== null) return { object: settlerTemplate.clone(), owned: false };
  const group = new Group();

  const skin = (colour: number): MeshLambertNodeMaterial => {
    /* 🔴 `MeshLambertNodeMaterial` with a `colorNode`, which is what the turf
       house, the driftwood and the torch posts already draw with. **A material
       the running game does not already compile is the thing that blacked out
       both environments**, and a shader cannot be checked on this workstation
       at all. */
    const material = new MeshLambertNodeMaterial();
    material.colorNode = uniform(new Color(colour));
    return material;
  };

  const torso = new Mesh(
    new BoxGeometry(BODY.width, BODY.torsoHeight, BODY.depth),
    skin(TORSO_COLOUR),
  );
  /* ⚠️ Box geometry is centred on its own middle; the group's origin is the
     FEET, so everything is lifted by half its height. Getting this backwards
     buries every body to the waist. */
  torso.position.y = BODY.torsoHeight / 2;
  group.add(torso);

  const head = new Mesh(
    new BoxGeometry(BODY.headSize, BODY.headSize, BODY.headSize),
    skin(HEAD_COLOUR),
  );
  head.position.y = BODY.torsoHeight + BODY.headSize / 2;
  group.add(head);

  /* ✅ Every box and every material here was made by this call, so this body
     is the one kind that may free them. */
  return { object: group, owned: true };
}

/**
 * 🧍 **Everybody else, drawn and kept up to date.**
 *
 * ⚠️ **It owns a `Group` and nothing else owns what is inside it.** Add that
 * group to the scene once; this fills and empties it.
 */
/**
 * 📐 **How big a carried sheep is drawn, against its real size** — T261.
 *
 * ⚠️ **A DIAL, and haugur's eye decides it.** A sheep is 1.07 m long and a
 * settler is 1.7 m tall; carried at full size it is an absurd armful, so this
 * is the *"a person carrying a lamb"* reading rather than a measurement.
 */
const CARRIED_SCALE = 0.62;

/** 🛏️ Half a body: a lying body's feet go this far toward the foot of the bed. */
const LYING_HALF = 0.85;
/** 🛏️ And this far up, so the back rests on the teppi rather than in it. */
const LYING_LIFT = 0.12;

/**
 * 🐣 **The drawn size of whatever is in somebody's arms** — protocol 49. A
 * lamb is its own fraction of the armful; `undefined` is a grown animal.
 * Exported so a test can reach it.
 */
export function carriedScale(lamb: number | undefined): number {
  return CARRIED_SCALE * (lamb ?? 1);
}

/** 📐 How far in front of the chest the animal sits, in metres. */
const CARRIED_FORWARD = 0.32;

/**
 * 📐 **How high up the body the animal is held, as a share of the torso.**
 *
 * ⚠️ **A DIAL.** At 0.62 the sheep covered the carrier's chin; this holds it at
 * chest height with the face clear. 👤 **His eye decides it.**
 */
const CARRIED_HEIGHT = 0.52;

export class OtherPlayers {
  readonly group = new Group();
  private readonly bodies = new Map<string, Body>();
  /**
   * 🙌 **Where a sheep for somebody's arms comes from** — T261.
   *
   * 🔴 **Injected rather than imported**, so this module still knows nothing
   * about creatures and `players-dispose.test.ts` can keep importing it without
   * a GLTF loader. ⚠️ **It may answer `null` while the model is still loading**,
   * which is why it is asked again on every beat rather than once.
   */
  private lendSheep: (() => Object3D | null) | null = null;
  private warnedNoLender = false;
  private warnedLendFailed = false;
  private warnedNoSheep = false;
  /**
   * 🔴 **Where the scene's origin is, in world metres** — and **the reason
   * nobody could see anybody on 2026-09-07.**
   *
   * 🎯 **haugur, with both windows open on dev:** *"dont see nobody."*
   *
   * ⚠️ **The world is 1:1 Iceland: coordinates are ISN93 metres, around 347 000
   * and 396 000.** Float32 has about seven significant digits, so **everything
   * is drawn relative to an origin near the player** (`renderOrigin` in
   * `main.ts`) — the camera itself subtracts it. **A body placed at its true
   * coordinate is therefore about 347 KILOMETRES outside the visible world**,
   * and there is nothing to see and nothing to warn you.
   *
   * 🔴 **THE SAME SHAPE AS THE SIGN IN THE SKY**: *a number in world metres and
   * a number in render metres look identical.* ✅ **Say which frame a number is
   * in, every time** — {@link Body} holds WORLD metres and {@link place} is the
   * only thing that ever writes a render position.
   */
  private origin = { x: 0, y: 0, z: 0 };

  /**
   * Put a body on screen. 🔴 The ONLY place a render position is written.
   *
   * 🔴 **This ASSIGNS `rotation.y`, so the root of a body may never carry a
   * baked-in rotation of its own.** The settler's corrective half-turn lives on
   * the node inside it for exactly this reason — see `loadSettler`. A
   * correction placed on the root is silently wiped on the first frame, and the
   * symptom is everybody drawn 180° from where they are really looking.
   */
  private place(body: Body): void {
    /**
     * 🛏️ **Lying in a bed** — D133. The root is tipped back a quarter turn
     * (order YXZ: the yaw first, then the tip), which turns the face up and puts
     * the head BEHIND the feet — toward the headboard, because a sleeper's yaw
     * faces the foot of the bed. ⚠️ The body's pivot is its FEET, so it is
     * moved half a body toward the foot, or the head would hang off the bed.
     */
    const lying = body.lying;
    const fx = lying ? -Math.sin(body.yaw) * LYING_HALF : 0;
    const fz = lying ? -Math.cos(body.yaw) * LYING_HALF : 0;
    body.object.position.set(
      body.x + fx - this.origin.x,
      body.y + (lying ? LYING_LIFT : 0) - this.origin.y,
      body.z + fz - this.origin.z,
    );
    body.object.rotation.order = 'YXZ';
    body.object.rotation.x = lying ? Math.PI / 2 : 0;
    body.object.rotation.y = body.yaw;
  }

  constructor() {
    /* 🗿 Fetch the settler now, so it is ready before anybody walks up.
       ⚠️ A body built before it lands gets the stand-in and is replaced when
       the model arrives — the same gate `burmesh.ts` uses, and the fault it
       records is a SECOND late thing being given its own gate beside the first. */
    loadSettler();
    waitingForSettler.push(() => {
      this.reskin();
    });
  }

  /**
   * 🗿 Swap every stand-in for the real model, once it has arrived.
   *
   * 🔴 **Without this, everybody who was already on screen stays a box
   * forever** — which is exactly the fault `burmesh.ts` names: the model and
   * the pictures both arrive late, and the second one was forgotten.
   */
  private reskin(): void {
    for (const body of this.bodies.values()) {
      this.group.remove(body.object);
      /* ⚠️ Safe here, and only because these are stand-ins: `reskin` runs the
         moment the template lands, so every body it touches was built without
         one and owns its own boxes. */
      disposeBody(body.object, body.owned);
      const built = buildBody();
      this.group.add(built.object);
      (body as { object: Object3D }).object = built.object;
      body.owned = built.owned;
      /* 🦵 The new copy has its own pivots — see {@link LEG_LEFT}. */
      body.left = built.object.getObjectByName(LEG_LEFT) ?? null;
      body.right = built.object.getObjectByName(LEG_RIGHT) ?? null;
      /**
       * 🙌 **FORGET THE ANIMAL, so the next frame puts it in the NEW arms** —
       * T261.
       *
       * 🔴 **This is the fault this very method warns about, arriving a third
       * time.** Its own comment says *"the model and the pictures both arrive
       * late, and the second one was forgotten"* — and the sheep is a third
       * late arrival. The animal was attached to the body that is being thrown
       * away, **and `held` still pointed at it**, so `dressArms` believed the
       * arms were full and never filled the new ones.
       *
       * ⚠️ **From the outside that is indistinguishable from *carrying is not
       * drawn at all***, which is exactly what it looked like for an hour.
       *
       * 🔴 **Nulled, never disposed.** It was borrowed from the flock's
       * template; freeing it would break every sheep in the world — the same
       * rule the settler clone keeps two lines above.
       */
      body.held = null;
      this.place(body);
    }
  }

  /**
   * 🔴 **What the server just said, replacing everything.**
   *
   * ⚠️ **The list is the whole truth, including when it is empty.** Anybody not
   * in it has gone, and their body goes with them — *"a frozen body and a
   * departed body look identical"*, so the only thing that removes one is being
   * left out of a message.
   */
  /**
   * 🙌 **Tell this module where to get a sheep** — T261.
   *
   * ⚠️ **Call it once at start-up.** Until it is called nobody's arms are ever
   * drawn, and nothing warns you — which is why the wiring lives beside the
   * flock's own creation in `main.ts`.
   */
  setSheepLender(lend: () => Object3D | null): void {
    this.lendSheep = lend;
  }

  /**
   * 🙌 **Put an animal in this person's arms, or take it away** — T261.
   *
   * ⚠️ **Asked every beat**, because {@link lendSheep} answers `null` until the
   * model has loaded. 🔴 **Caching that `null` would leave arms empty for the
   * whole session** with nothing anywhere saying why.
   */
  private dressArms(body: Body): void {
    if (body.carrying === null) {
      if (body.held !== null) {
        /* 🔴 Detached, never disposed — see {@link Body.held}. */
        body.object.remove(body.held);
        body.held = null;
      }
      return;
    }
    if (body.held !== null) {
      /* 🐣 Asked every beat, so a lamb that was picked up — or grows — is
         drawn at its size without being lent a second body. */
      body.held.scale.setScalar(carriedScale(body.carryingLamb));
      return;
    }
    if (this.lendSheep === null) {
      /* 🔴 Said ONCE and loudly. Nobody called {@link setSheepLender}, so no
         arms will ever be drawn — and silence is what made this cost an hour. */
      if (!this.warnedNoLender) {
        this.warnedNoLender = true;
        console.error('[players] somebody is carrying, but no sheep lender was set — T261');
      }
      return;
    }
    let sheep: Object3D | null = null;
    try {
      sheep = this.lendSheep();
    } catch (err: unknown) {
      /* 🔴 **Swallowed deliberately, and REPORTED.** A throw here would abort
         `setPlayers` for every beat afterwards — so nobody's position would
         update again and it would read as the server having stopped. */
      if (!this.warnedLendFailed) {
        this.warnedLendFailed = true;
        console.error('[players] the sheep lender threw — T261', err);
      }
      return;
    }
    if (sheep === null) {
      /* ⚠️ Ordinary while the model is still loading; only worth saying once. */
      if (!this.warnedNoSheep) {
        this.warnedNoSheep = true;
        console.info('[players] no sheep body available yet — will ask again — T261');
      }
      return;
    }

    /**
     * 📐 **In front of the chest, and small enough to be an armful.**
     *
     * ⚠️ **Measured against the settler rather than guessed**: the body is
     * {@link BODY.torsoHeight} tall, so chest height is about two thirds of it.
     * 🔴 **A sheep at full size is 1.07 m long against a 1.7 m person** — it is
     * scaled down because a person carries a lamb-sized bundle, not a ewe at
     * arm's length. **A dial, and his eye decides it.**
     */
    sheep.scale.setScalar(carriedScale(body.carryingLamb));
    /**
     * 🔴 **MINUS z is IN FRONT.** A body with yaw 0 faces −z — the same
     * convention `lookVector` uses — and the template is turned by π inside
     * `sized` to make that true.
     *
     * ⚠️ **The first version used +z and put the sheep behind the settler's
     * back**, where the body hid it completely. 🔴 **From the camera that is
     * indistinguishable from *the sheep is not drawn at all*** — which is the
     * very thing the picture was taken to check. **Found by baking it.**
     */
    sheep.position.set(0, BODY.torsoHeight * CARRIED_HEIGHT, -CARRIED_FORWARD);
    /* 🐑 Turned across the body, the way anybody carries an animal — head to
       one side rather than pointing where you walk. */
    sheep.rotation.y = Math.PI / 2;
    body.object.add(sheep);
    body.held = sheep;
  }

  setPlayers(players: PlayersMessage['players']): void {
    const seen = new Set<string>();
    for (const p of players) {
      seen.add(p.id);
      const existing = this.bodies.get(p.id);
      if (existing === undefined) {
        /* 🔴 A new body starts WHERE IT IS, not where the last one was. Easing
           it in from the origin would send every arrival sliding across
           Iceland. */
        /* ⚠️ No position written here — {@link place} owns that, and it needs
           the render origin. `update` runs before every frame is drawn. */
        const built = buildBody();
        this.group.add(built.object);
        this.bodies.set(p.id, {
          object: built.object,
          owned: built.owned,
          /* ⚠️ `?? null` rather than `?`, so an older server that sends no
             field at all and a newer one that sends `null` mean the same
             thing here — nameless — and only one of the two has to be
             handled everywhere downstream. */
          name: p.name ?? null,
          x: p.x,
          y: p.y,
          z: p.z,
          yaw: p.yaw,
          targetX: p.x,
          targetY: p.y,
          targetZ: p.z,
          targetYaw: p.yaw,
          walked: 0,
          left: built.object.getObjectByName(LEG_LEFT) ?? null,
          right: built.object.getObjectByName(LEG_RIGHT) ?? null,
          /* 🙌 T261 — `?? null` for the same reason `name` uses it: an older
             server sends no field and a newer one sends `null`, and both mean
             empty arms. */
          carrying: p.carrying ?? null,
          carryingLamb: p.carryingLamb,
          lying: p.lying === true,
          held: null,
        });
        continue;
      }
      existing.targetX = p.x;
      existing.targetY = p.y;
      existing.targetZ = p.z;
      existing.targetYaw = p.yaw;
      /* 🧍 T176. ⚠️ **Refreshed on every beat, not only on arrival** — the
         name is not a property of the body being built, and a body that kept
         whatever it was first told would be right until it was not. */
      existing.name = p.name ?? null;
      /* 🙌 T261, refreshed every beat exactly as the name is. */
      existing.carrying = p.carrying ?? null;
      existing.carryingLamb = p.carryingLamb;
      existing.lying = p.lying === true;
    }

    for (const [id, body] of this.bodies) {
      if (seen.has(id)) continue;
      this.group.remove(body.object);
      /* 🙌 T261: the animal goes with them. 🔴 **Detached, NEVER disposed** —
         it is borrowed from the flock's template, and freeing it would break
         every sheep in the world, which is the settler bug at a new subject. */
      if (body.held !== null) {
        body.object.remove(body.held);
        body.held = null;
      }
      /* 🔴 THE LINE THE BUG WAS IN. This used to free the geometry and material
         of a body that only BORROWED them from the template, so one person
         leaving made everybody who arrived afterwards invisible. */
      disposeBody(body.object, body.owned);
      this.bodies.delete(id);
    }
  }

  /**
   * Move every body a little closer to where it really is.
   *
   * @param dt Seconds since the last frame. ⚠️ **Seconds, not milliseconds** —
   *   a thousand-fold error here reads as bodies that teleport.
   */
  update(dt: number, origin: { x: number; y: number; z: number }): void {
    /* 🔴 Kept even when nobody is here, so the first body to arrive is placed
       in the right frame rather than 347 km away for a frame. */
    this.origin = { x: origin.x, y: origin.y, z: origin.z };
    if (this.bodies.size === 0) return;

    /**
     * 🙌 **Give and take away the animals in people's arms** — T261.
     *
     * 🔴 **HERE, PER FRAME — NOT IN `setPlayers`, AND THAT IS A REAL BUG THAT
     * WAS FOUND BY BAKING A PICTURE.**
     *
     * ⚠️ **The `players` message is only sent when the list CHANGES** — its own
     * protocol note says so, and a still room costs nothing. 🔴 **So a person
     * standing still holding a sheep produces no further messages**, and a
     * client that finished loading the sheep model *after* that last message
     * **would never put the animal in their arms at all.**
     *
     * 📏 **Measured: `carriedBody()` was asked exactly TWICE**, both times
     * before the model had loaded, and never again — so the arms stayed empty
     * for the whole session while the server said `carrying: "kind"` throughout.
     *
     * 👉 ***A retry that lives on the message it is retrying can only run when
     * the message arrives.*** ✅ **It is cheap here**: it returns on the first
     * line for anybody carrying nothing, which is nearly everybody nearly
     * always.
     */
    for (const body of this.bodies.values()) this.dressArms(body);
    const t = approach(CATCH_UP_SECONDS, dt);
    for (const body of this.bodies.values()) {
      const wasX = body.x;
      const wasZ = body.z;
      body.x += (body.targetX - body.x) * t;
      body.y += (body.targetY - body.y) * t;
      body.z += (body.targetZ - body.z) * t;
      /* 🔴 The short way round — see `turnTowards`. A plain lerp here spins a
         person 358° every time they cross south. */
      body.yaw = turnTowards(body.yaw, body.targetYaw, t);
      /* 🚶 How far they moved along the ground this frame. ⚠️ **Horizontal
         only** — falling is not walking, and a player dropping off a wall
         should not windmill their legs on the way down. */
      body.walked += Math.hypot(body.x - wasX, body.z - wasZ);
      const swing = legSwing(body.walked);
      if (body.left) body.left.rotation.x = swing;
      if (body.right) body.right.rotation.x = -swing;
      this.place(body);
    }
  }

  /** How many other people are on screen. ⚠️ For tests and for the log. */
  get count(): number {
    return this.bodies.size;
  }

  /**
   * 🧍 **Everybody who has a name, and where to write it** — T176, protocol 27.
   *
   * 🎯 **haugur, 2026-09-09:** *"i need to have players name above the head of
   * their carachter."*
   *
   * 🔴 **A body with no name is LEFT OUT rather than given a placeholder.**
   * Development is entirely anonymous, so on dev this returns nothing at all
   * and no name is drawn anywhere — which is correct. **A head labelled
   * *Anonymous* invents a person, and invents the same one on every head.**
   *
   * ⚠️ **The position is the SMOOTHED one, not the target.** The name has to
   * ride the body it belongs to; reading `targetY` would leave it lagging a
   * hand's breadth ahead of the head on every step.
   *
   * ⚠️ **World metres, not render metres** — the same frame street names are
   * given in, so one projection serves both. See `Hud.updateNameplates`.
   */
  /**
   * 🪢 **Where this player's right hand is, in world metres, or `null`** — D128.
   *
   * ⚠️ **The settler has no hand bone** — it is one body and two leg pivots — so
   * the hand is a point on the body: waist height, a little to the right and in
   * front, turned with the body. `rotation.y = yaw` and **−z is in front**,
   * the same frame `dressArms` uses.
   */
  handOf(id: string): { x: number; y: number; z: number } | null {
    const body = this.bodies.get(id);
    if (body === undefined) return null;
    const lx = 0.3;
    const lz = -0.25;
    const c = Math.cos(body.yaw);
    const sn = Math.sin(body.yaw);
    return { x: body.x + lx * c + lz * sn, y: body.y + 1.0, z: body.z - lx * sn + lz * c };
  }

  nameplates(): readonly { id: string; name: string; x: number; y: number; z: number }[] {
    const out: { id: string; name: string; x: number; y: number; z: number }[] = [];
    for (const [id, body] of this.bodies) {
      /* 🔴 WHETHER there is a name, and HOW HIGH it floats, are both decided in
         `nameplate.ts` — where a test can reach them. This file needs a
         renderer and a network fetch, so a rule left here is a rule nothing
         checks. The same split `presence.ts` made, for the same reason. */
      const at = nameplateAt(body.name, body);
      if (at === null) continue;
      out.push({ id, name: body.name as string, x: at.x, y: at.y, z: at.z });
    }
    return out;
  }
}

/**
 * Give a departed body's geometry and material back — **if they were its own.**
 *
 * 🔴 **`owned` is not optional and has no default, deliberately.** A default of
 * `true` would restore the exact bug this parameter exists to stop, silently, at
 * the next call site somebody adds. See {@link Body.owned}.
 *
 * ⚠️ **A borrowed body leaks nothing.** Its geometry and material belong to
 * `settlerTemplate`, which is loaded once and lives as long as the page.
 */
export function disposeBody(object: Object3D, owned: boolean): void {
  if (!owned) return;
  object.traverse((o) => {
    const mesh = o as Mesh;
    if (mesh.isMesh !== true) return;
    mesh.geometry.dispose();
    const material = mesh.material as { dispose?: () => void };
    material.dispose?.();
  });
}
